import {
  YES_NO,
  applicantSection,
  deMinimisAttachments,
  feeInfo,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * B4. Wniosek o udzielenie dotacji celowej z budżetu gminy na wymianę źródła ciepła — art. 403 ust. 4–6 POŚ. Kryteria,
 * tryb i lista załączników wynikają WYŁĄCZNIE z uchwały rady gminy (art. 403 ust. 5), więc to formularz ogólny:
 * pola i załączniki do potwierdzenia z wydziałem i dostosowania do lokalnej uchwały. Brak wzoru ogólnopolskiego;
 * układ jak wzór Pruszkowa (uchwała XXXIII.338.2021) pobrany 2026-10-22 — https://bip.um.pruszkow.pl/attachments/download/22516
 * (I. wnioskodawca, II. lokalizacja zadania, III. obecny i planowany sposób ogrzewania, IV. wykaz dokumentów,
 * oświadczenia wnioskodawcy). Program „Czyste Powietrze” (WFOŚiGW) składa się poza gminą — pole informacyjne z linkami
 * z katalogu.
 */
const ENTITY: EbiCondition = { field: "rodzajWnioskodawcy", in: ["wspolnota", "podmiot"] };
const NEW_GAS: EbiCondition = { field: "zrodloNowe", equals: "gazowe" };

const B4: IEbiFormDefinition = {
  code: "B4",
  version: 1,
  title: "Wniosek o udzielenie dotacji celowej na wymianę źródła ciepła",
  category: EEbiFormCategory.Environment,
  office: EEbiFormOffice.Municipality,
  description: "Gminna dotacja na wymianę pieca (kotła) na ekologiczne źródło ciepła — na zasadach uchwały rady gminy.",
  keywords: ["dotacja", "piec", "kocioł", "wymiana pieca", "źródło ciepła", "ogrzewanie", "ochrona powietrza", "kopciuch"],
  organ: "Wójt / Burmistrz / Prezydent Miasta",
  legalBasis:
    "art. 403 ust. 4–6 ustawy z dnia 27 kwietnia 2001 r. — Prawo ochrony środowiska; uchwała rady gminy w sprawie zasad udzielania dotacji celowej na dofinansowanie inwestycji służących ochronie powietrza",
  request:
    "Wnoszę o udzielenie dotacji celowej z budżetu gminy na dofinansowanie wymiany źródła ciepła w nieruchomości wskazanej poniżej, na zasadach określonych w uchwale rady gminy.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty; pełnomocnictwo — 17 zł.",
  deadlineLabel: "wg uchwały",
  deadline: "Według uchwały rady gminy (terminy naboru i rozpatrywania wniosków); dotacja jest udzielana na podstawie umowy.",
  sections: [
    applicantSection("I. Wnioskodawca"),
    representationSection(),
    {
      code: "rodzajPodmiotu",
      title: "Rodzaj wnioskodawcy",
      fields: [
        {
          code: "rodzajWnioskodawcy",
          type: "radio",
          label: "Wniosek składa",
          required: true,
          defaultValue: "osobaFizyczna",
          options: [
            { value: "osobaFizyczna", label: "osoba fizyczna — właściciel lub współwłaściciel budynku albo lokalu" },
            { value: "wspolnota", label: "wspólnota mieszkaniowa albo spółdzielnia mieszkaniowa" },
            { value: "podmiot", label: "przedsiębiorca albo inny podmiot spoza sektora finansów publicznych" },
          ],
        },
        {
          code: "podmiotNazwa",
          type: "text",
          label: "Nazwa wspólnoty, spółdzielni lub podmiotu",
          required: true,
          visibleWhen: ENTITY,
          maxLength: 300,
          hint: "Podmiot, w imieniu którego składasz wniosek (np. jako członek zarządu lub zarządca).",
        },
        { code: "podmiotNip", type: "nip", label: "NIP podmiotu", visibleWhen: ENTITY, width: "half" },
      ],
    },
    {
      code: "lokalizacja",
      title: "II. Lokalizacja zadania",
      fields: [
        { code: "adresZadania", type: "text", label: "Adres wymiany źródła ogrzewania (ulica, numer, miejscowość)", required: true, maxLength: 300 },
        { code: "obreb", type: "text", label: "Obręb ewidencyjny", width: "half", maxLength: 100 },
        { code: "dzialka", type: "text", label: "Numer działki ewidencyjnej", width: "half", maxLength: 100, placeholder: "np. 125/4" },
        {
          code: "tytulPrawny",
          type: "radio",
          label: "Tytuł prawny do nieruchomości",
          required: true,
          options: [
            { value: "wlasnosc", label: "własność" },
            { value: "wspolwlasnosc", label: "współwłasność" },
            { value: "uzytkowanieWieczyste", label: "użytkowanie wieczyste" },
            { value: "inny", label: "inny" },
          ],
        },
        { code: "tytulPrawnyInny", type: "text", label: "Tytuł prawny (inny — jaki)", required: true, visibleWhen: { field: "tytulPrawny", equals: "inny" }, maxLength: 200 },
        {
          code: "nrKw",
          type: "text",
          label: "Numer księgi wieczystej",
          maxLength: 30,
          placeholder: "np. LU1I/00012345/6",
          hint: "Gdy go podasz, nie musisz dołączać dokumentu potwierdzającego tytuł prawny.",
        },
        {
          code: "zgodaWspolwlascicieli",
          type: "radio",
          label: "Czy nieruchomość ma współwłaścicieli albo wnioskodawca nie jest jej właścicielem?",
          required: true,
          options: YES_NO,
        },
        {
          code: "typBudynku",
          type: "radio",
          label: "Typ budynku",
          required: true,
          options: [
            { value: "mieszkalny", label: "mieszkalny" },
            { value: "mieszkalnoUslugowy", label: "mieszkalno-usługowy" },
            { value: "mieszkalnoHandlowy", label: "mieszkalno-handlowy" },
            { value: "uslugowy", label: "usługowy" },
            { value: "handlowy", label: "handlowy" },
            { value: "handlowoUslugowy", label: "handlowo-usługowy" },
          ],
        },
        { code: "powierzchniaUzytkowa", type: "number", label: "Powierzchnia użytkowa budynku [m²]", decimals: 2, min: 0, max: 100000, width: "third" },
        { code: "liczbaLokali", type: "number", label: "Liczba lokali w budynku", min: 1, max: 9999, width: "third" },
        { code: "rokBudowy", type: "number", label: "Rok budowy budynku", min: 1800, max: 2100, width: "third" },
        {
          code: "termomodernizacja",
          type: "checkboxes",
          label: "Termomodernizacja budynku",
          options: [
            { value: "sciany", label: "zastosowana izolacja ścian zewnętrznych" },
            { value: "stropodach", label: "zastosowana izolacja stropodachu" },
            { value: "stolarka", label: "wymieniona stolarka okienna" },
            { value: "brak", label: "brak termomodernizacji" },
          ],
        },
        {
          code: "dzialalnosc",
          type: "radio",
          label: "Czy w budynku (lokalu) prowadzona jest działalność gospodarcza lub rolnicza?",
          required: true,
          options: YES_NO,
          hint: "Odpowiedź „Tak” oznacza pomoc de minimis — dołączysz formularz i zaświadczenia (art. 403 ust. 5b POŚ).",
        },
      ],
    },
    {
      code: "obecneZrodlo",
      title: "III A. Obecny sposób ogrzewania",
      fields: [
        {
          code: "zrodloObecne",
          type: "radio",
          label: "Źródło ciepła",
          required: true,
          options: [
            { value: "staly", label: "kocioł lub piec na paliwo stałe" },
            { value: "olejowe", label: "olejowe" },
            { value: "gazowe", label: "gazowe" },
            { value: "inne", label: "inne" },
          ],
        },
        { code: "zrodloObecneInne", type: "text", label: "Źródło ciepła (inne)", required: true, visibleWhen: { field: "zrodloObecne", equals: "inne" }, maxLength: 200 },
        {
          code: "paliwoZuzycie",
          type: "text",
          label: "Rodzaj paliwa i średnioroczne zużycie",
          maxLength: 200,
          placeholder: "np. węgiel — 4 t/rok, drewno — 3 m³/rok",
        },
        { code: "rokProdukcjiKotla", type: "number", label: "Rok produkcji kotła", min: 1900, max: 2100, width: "half" },
        { code: "klasaKotla", type: "text", label: "Klasa kotła", width: "half", maxLength: 50 },
      ],
    },
    {
      code: "noweZrodlo",
      title: "III B. Sposób ogrzewania po modernizacji",
      fields: [
        {
          code: "zrodloNowe",
          type: "radio",
          label: "Nowe źródło ciepła",
          required: true,
          options: [
            { value: "siec", label: "miejska sieć ciepłownicza" },
            { value: "elektryczne", label: "ogrzewanie elektryczne" },
            { value: "gazowe", label: "kocioł gazowy" },
            { value: "olejowe", label: "kocioł olejowy" },
            { value: "pompa", label: "pompa ciepła" },
            { value: "inne", label: "inne" },
          ],
        },
        { code: "zrodloNoweInne", type: "text", label: "Nowe źródło ciepła (inne)", required: true, visibleWhen: { field: "zrodloNowe", equals: "inne" }, maxLength: 200 },
        { code: "urzadzenie", type: "text", label: "Planowane urządzenie (producent, nazwa handlowa, model)", maxLength: 300 },
        { code: "mocUrzadzenia", type: "number", label: "Moc nominalna urządzenia [kW]", decimals: 2, min: 0, max: 10000, width: "third" },
        { code: "cenaUrzadzenia", type: "number", label: "Cena urządzenia brutto [zł]", decimals: 2, min: 0, max: 10000000, width: "third" },
        { code: "terminRealizacji", type: "text", label: "Planowany termin realizacji zadania", width: "third", maxLength: 100 },
        { code: "przylaczeGazowe", type: "radio", label: "Czy istnieje przyłącze gazowe z sieci do budynku?", required: true, visibleWhen: NEW_GAS, options: YES_NO },
      ],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia wnioskodawcy",
      fields: [
        {
          code: "oswSrodki",
          type: "declaration",
          label: "Oświadczam, że zostałam/zostałem poinformowana/poinformowany, że gmina może nie uwzględnić wniosku w razie wyczerpania środków finansowych przeznaczonych na ten cel.",
          required: true,
        },
        {
          code: "oswUchwala",
          type: "declaration",
          label: "Oświadczam, że zapoznałam/zapoznałem się z uchwałą rady gminy w sprawie zasad udzielania dotacji celowej na dofinansowanie wymiany źródła ciepła.",
          required: true,
        },
        feeInfo(
          "infoCzystePowietrze",
          "Program „Czyste Powietrze” (Wojewódzki Fundusz Ochrony Środowiska i Gospodarki Wodnej) to odrębna ścieżka dofinansowania — wniosek składa się w generatorze wniosków https://gwd.nfosigw.gov.pl/ (informacje: https://www.gov.pl/web/gov/skorzystaj-z-programu-czyste-powietrze), nie tym formularzem. W złożeniu wniosku pomoże gminny punkt konsultacyjno-informacyjny programu.",
        ),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "tytulPrawnyPlik",
      label: "Dokument potwierdzający tytuł prawny do nieruchomości lub lokalu",
      hint: "Np. akt notarialny, wypis z rejestru gruntów. Nie jest wymagany, gdy podasz numer księgi wieczystej.",
      requiredWhen: { not: { field: "nrKw", filled: true } },
    },
    {
      code: "zgodaWspolwlascicieliPlik",
      label: "Zgoda współwłaścicieli albo właściciela nieruchomości na realizację zadania",
      visibleWhen: { field: "zgodaWspolwlascicieli", equals: "tak" },
      required: true,
    },
    {
      code: "uchwalaWspolnoty",
      label: "Uchwała wspólnoty (spółdzielni) w sprawie zadania oraz zestawienie lokali",
      visibleWhen: { field: "rodzajWnioskodawcy", equals: "wspolnota" },
      required: true,
    },
    ...deMinimisAttachments({ field: "dzialalnosc", equals: "tak" }),
    {
      code: "zdjecieZrodla",
      label: "Dokumentacja fotograficzna obecnego źródła ogrzewania",
      hint: "Gdy wymaga jej uchwała rady gminy (praktyka urzędów).",
    },
    {
      code: "specyfikacja",
      label: "Specyfikacja techniczna planowanego urządzenia, oferta lub kosztorys",
      hint: "Np. potwierdzenie klasy 5 lub zgodności z ekoprojektem — gdy wymaga ich uchwała (praktyka urzędów).",
    },
    {
      code: "gazUmowa",
      label: "Kopia umowy kompleksowej na dostarczanie paliwa gazowego albo warunki przyłączenia do sieci gazowej",
      hint: "Nieobowiązkowo (praktyka urzędów).",
      visibleWhen: { all: [NEW_GAS, { field: "przylaczeGazowe", equals: "nie" }] },
    },
    {
      code: "pozwolenieBudowy",
      label: "Pozwolenie na budowę albo zgłoszenie robót budowlanych",
      hint: "Gdy prace (np. instalacja gazowa) tego wymagają — nieobowiązkowo (praktyka urzędów).",
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default B4;
